import { Component, computed, inject } from '@angular/core';
import { MACHINE_DEFS } from '@sim/content/machine-defs';
import type { MachineKind } from '@sim/core/entities';
import { CREATION_KINDS, isCreationKind } from '@sim/content/creation-kinds';
import { SUBJECT_DEF } from '@sim/content/subject-kind';
import type { BuildKind } from '@sim/content/subject-kind';
import { unlockGateFor } from '@sim/content/unlock-gates';
import { buildCost } from '@sim/core/editing';
import { isGateSatisfied } from '@sim/core/gate-condition';
import type { GridPos } from '@sim/core/types';
import { BuildToolService } from '@app/core/services/build-tool.service';
import type { ToolId } from '@app/core/services/build-tool.service';
import { buildRequestFor } from '@app/core/build-request';
import { canEraseCell } from '@app/core/erase-policy';
import { GameViewportService } from '@app/core/services/game-viewport.service';
import { OnboardingService } from '@app/core/services/onboarding.service';
import { SimEngineService } from '@app/core/services/sim-engine.service';
import { TileThumbnailService } from '@app/core/services/tile-thumbnail.service';
import type { ThumbnailKey } from '@app/core/services/tile-thumbnail.service';
import { UiStateService } from '@app/core/services/ui-state.service';
import { DRAG_THRESHOLD_PX, exceedsDragThreshold } from '@render/pixi-app-geometry';
import { ToolButtonComponent } from './tool-button.component';

interface ToolButton {
  id: ToolId;
  label: string;
  detail: string;
  cost: number | null;
  thumbnailKey?: ThumbnailKey;
  tooltip?: string;
  docsUrl?: string;
}

const SECTION_CLASSES =
  'flex flex-row items-center gap-2 landscape:w-full landscape:flex-col landscape:items-stretch';
const DIVIDER_CLASSES =
  'mx-1 h-8 w-px shrink-0 bg-slate-700 landscape:mx-0 landscape:my-1 landscape:h-px landscape:w-full';
const HEADER_CLASSES =
  'hidden px-1 text-[11px] font-semibold tracking-wide text-slate-500 uppercase landscape:block';

@Component({
  selector: 'app-toolbar',
  imports: [ToolButtonComponent],
  template: `
    <nav
      class="pointer-events-auto relative z-[35] flex flex-row items-center gap-2 overflow-x-auto border-t border-slate-800 bg-slate-900 px-2 py-2 landscape:h-full landscape:w-60 landscape:flex-col landscape:items-stretch landscape:overflow-x-hidden landscape:overflow-y-auto landscape:border-t-0 landscape:border-r landscape:py-3"
      aria-label="Build tools"
    >
      @for (group of groups(); track group.title; let last = $last) {
        <h2 [class]="HEADER_CLASSES">{{ group.title }}</h2>
        <section [class]="SECTION_CLASSES">
          @for (button of group.buttons; track button.id) {
            <app-tool-button
              [id]="button.id"
              [label]="button.label"
              [detail]="button.detail"
              [cost]="button.cost"
              [thumbnailUrl]="thumbnailUrl(button.thumbnailKey)"
              [tooltip]="button.tooltip"
              [docsUrl]="button.docsUrl"
              [affordable]="isAffordable(button)"
              (pointerdown)="onDragPointerDown($event, button)"
            />
          }
        </section>
        @if (!last) {
          <div [class]="DIVIDER_CLASSES" aria-hidden="true"></div>
        }
      }
    </nav>
    @if (ui.toolbarDrag(); as drag) {
      <div
        class="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-1/2 rounded-lg border border-sky-400 bg-sky-950/90 px-2.5 py-1.5 text-sm font-semibold text-sky-100 shadow-xl"
        [style.left.px]="drag.x"
        [style.top.px]="drag.y"
      >
        {{ dragLabel(drag.kind) }}
      </div>
    }
  `,
  host: {
    '(document:keydown)': 'onKeydown($event)',
    '(document:pointermove)': 'onDocPointerMove($event)',
    '(document:pointerup)': 'onDocPointerUp($event)',
  },
})
export class ToolbarComponent {
  protected readonly tools = inject(BuildToolService);
  protected readonly ui = inject(UiStateService);
  private readonly thumbnails = inject(TileThumbnailService);
  private readonly engine = inject(SimEngineService);
  private readonly onboarding = inject(OnboardingService);
  private readonly viewport = inject(GameViewportService);
  protected readonly SECTION_CLASSES = SECTION_CLASSES;
  protected readonly DIVIDER_CLASSES = DIVIDER_CLASSES;
  protected readonly HEADER_CLASSES = HEADER_CLASSES;
  private dragStart: { x: number; y: number } | null = null;
  private dragKind: BuildKind | null = null;

  protected readonly observableButtons = computed<ToolButton[]>(() => {
    const state = this.engine.state();
    return (['of', 'from', 'interval'] as const)
      .map((kind) => {
        const def = CREATION_KINDS[kind];
        return {
          id: kind,
          label: def.label,
          detail: def.description,
          cost: def.cost,
          thumbnailKey: kind,
          tooltip: def.expandedDescription,
          docsUrl: def.docsUrl,
        };
      })
      .filter((button) => isGateSatisfied(state, unlockGateFor(button.id)));
  });

  protected readonly operatorButtons = computed<ToolButton[]>(() => {
    const state = this.engine.state();
    return (Object.keys(MACHINE_DEFS) as MachineKind[])
      .map((kind) => {
        const def = MACHINE_DEFS[kind];
        return {
          id: kind,
          label: def.label,
          detail: def.description,
          cost: buildCost({ type: 'machine', kind }),
          thumbnailKey: kind,
          tooltip: def.expandedDescription,
          docsUrl: def.docsUrl,
        };
      })
      .filter((button) => isGateSatisfied(state, unlockGateFor(button.id)));
  });

  protected readonly subjectButtons = computed<ToolButton[]>(() => {
    const button: ToolButton = {
      id: 'subject',
      label: SUBJECT_DEF.label,
      detail: SUBJECT_DEF.description,
      cost: SUBJECT_DEF.cost,
      thumbnailKey: 'subject',
      tooltip: SUBJECT_DEF.expandedDescription,
      docsUrl: SUBJECT_DEF.docsUrl,
    };
    return isGateSatisfied(this.engine.state(), unlockGateFor('subject')) ? [button] : [];
  });

  protected readonly groups = computed(() =>
    [
      { title: 'Observables', buttons: this.observableButtons() },
      { title: 'Subjects', buttons: this.subjectButtons() },
      { title: 'Operators', buttons: this.operatorButtons() },
    ].filter((group) => group.buttons.length > 0),
  );

  protected thumbnailUrl(key: ThumbnailKey | undefined): string | undefined {
    return key ? this.thumbnails.thumbnails()[key] : undefined;
  }

  protected isAffordable(button: ToolButton): boolean {
    return button.cost === null || this.engine.state().economy.cash >= button.cost;
  }

  protected dragLabel(kind: BuildKind): string {
    if (kind === 'subject') return SUBJECT_DEF.label;
    return isCreationKind(kind) ? CREATION_KINDS[kind].label : MACHINE_DEFS[kind].label;
  }

  protected onDragPointerDown(event: PointerEvent, button: ToolButton): void {
    if (!this.isAffordable(button)) return;
    this.dragStart = { x: event.clientX, y: event.clientY };
    this.dragKind = button.id;
  }

  protected onDocPointerMove(event: PointerEvent): void {
    if (!this.dragKind || !this.dragStart) return;
    const pointer = { x: event.clientX, y: event.clientY };
    if (!this.ui.toolbarDrag() && !exceedsDragThreshold(this.dragStart, pointer, DRAG_THRESHOLD_PX))
      return;
    this.ui.toolbarDrag.set({ kind: this.dragKind, ...pointer });
  }

  protected onDocPointerUp(event: PointerEvent): void {
    const drag = this.ui.toolbarDrag();
    this.dragStart = null;
    this.dragKind = null;
    if (!drag) return;
    this.ui.toolbarDrag.set(null);
    const pos = this.viewport.cellAtClientPoint(event.clientX, event.clientY);
    if (!pos) return;
    const result = this.engine.place(buildRequestFor(drag.kind), pos);
    if (result.ok && drag.kind !== 'from') this.tools.selectCell(pos);
  }

  private canErase(pos: GridPos): boolean {
    return canEraseCell(this.engine, this.onboarding, pos);
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.repeat || event.ctrlKey || event.metaKey || event.altKey) return;
    const target = event.target as HTMLElement | null;
    if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;

    const key = event.key.toLowerCase();
    switch (key) {
      case 'escape':
        if (this.tools.selectedCell()) this.tools.selectCell(null);
        else this.tools.deselect();
        break;
      case 'delete':
      case 'backspace': {
        const pos = this.tools.selectedCell();
        if (pos && this.canErase(pos)) {
          event.preventDefault();
          this.engine.erase(pos);
          this.tools.selectCell(null);
        }
        break;
      }
    }
  }
}
